import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';

const UPITransferModal = ({ show, handleClose, mobileNumber, onSubmit }) => {
  const [formData, setFormData] = useState({
    upiId: '',
    beneficiaryName: '',
    amount: '',
    remark: ''
  });

  const [errors, setErrors] = useState({});
  const [verifying, setVerifying] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setFormData({
        upiId: '',
        beneficiaryName: '',
        amount: '',
        remark: ''
      });
      setErrors({});
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate UPI ID format
  const validateUpiId = (upiId) => {
    const upiRegex = /^[\w.-]+@[\w.-]+$/;
    return upiRegex.test(upiId);
  };

  // Verify UPI ID (mock verification)
  const handleVerifyUPI = () => {
    if (!formData.upiId) {
      setErrors(prev => ({ ...prev, upiId: 'Please enter UPI ID' }));
      return;
    }

    if (!validateUpiId(formData.upiId)) {
      setErrors(prev => ({ ...prev, upiId: 'Invalid UPI ID format' }));
      return;
    }

    setVerifying(true);
    
    // Simulate API call
    setTimeout(() => {
      setVerifying(false);
      // Mock verification success - set random name
      const mockNames = ['Rajesh Kumar', 'Amit Sharma', 'Priya Singh', 'Suresh Patel', 'Neha Verma'];
      const randomName = mockNames[Math.floor(Math.random() * mockNames.length)];
      setFormData(prev => ({ ...prev, beneficiaryName: randomName }));
      setErrors(prev => ({ ...prev, upiId: '' }));
    }, 1500);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.upiId.trim()) {
      newErrors.upiId = 'UPI ID is required';
    } else if (!validateUpiId(formData.upiId)) {
      newErrors.upiId = 'Invalid UPI ID format (e.g., user@paytm)';
    }

    if (!formData.beneficiaryName.trim()) {
      newErrors.beneficiaryName = 'Beneficiary name is required';
    }

    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    } else if (parseFloat(formData.amount) > 100000) {
      newErrors.amount = 'Amount cannot exceed ₹1,00,000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const transferData = {
        ...formData,
        mobileNumber,
        transactionDate: new Date().toISOString().split('T')[0]
      };
      onSubmit(transferData);
      handleClose();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="custom-modal-width">
      <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Modal.Title style={{ fontSize: '1.1rem' }}>
          <i className="bi bi-bank me-2"></i>
          UPI Transfer Details
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1.25rem' }}>
          <div className="mb-3 p-2" style={{ background: '#f8f9fa', borderRadius: '8px' }}>
            <small className="text-muted">Mobile Number:</small>
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ fontWeight: '600', color: '#1e293b' }}>
                Registered Mobile
              </span>
              <span style={{ fontSize: '1rem', fontWeight: '700', color: '#667eea' }}>
                {mobileNumber}
              </span>
            </div>
          </div>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>
                  UPI ID <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="upiId"
                  placeholder="Enter UPI ID (e.g., user@paytm)"
                  value={formData.upiId}
                  onChange={handleChange}
                  isInvalid={!!errors.upiId}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.upiId}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Format: username@provider
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Label className="d-block">&nbsp;</Form.Label>
              <Button
                variant="outline-primary"
                onClick={handleVerifyUPI}
                disabled={verifying || !formData.upiId}
                className="w-100"
                size="sm"
              >
                {verifying ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Verifying...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    Verify
                  </>
                )}
              </Button>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>
              Beneficiary Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="beneficiaryName"
              placeholder="Enter beneficiary name"
              value={formData.beneficiaryName}
              onChange={handleChange}
              isInvalid={!!errors.beneficiaryName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.beneficiaryName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Amount (₹) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              name="amount"
              placeholder="Enter amount"
              value={formData.amount}
              onChange={handleChange}
              isInvalid={!!errors.amount}
              min="1"
              max="100000"
              step="0.01"
            />
            <Form.Control.Feedback type="invalid">
              {errors.amount}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Minimum: ₹1 | Maximum: ₹1,00,000
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Remark <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="remark"
              placeholder="Enter remark or description"
              value={formData.remark}
              onChange={handleChange}
              maxLength={200}
            />
            <Form.Text className="text-muted">
              {formData.remark.length}/200 characters
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer style={{ padding: '1rem 1.25rem', borderTop: '1px solid #dee2e6' }}>
          <Button variant="secondary" onClick={handleClose} size="sm">
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            size="sm"
            style={{
              background: '#667eea',
              border: 'none'
            }}
          >
            <i className="bi bi-send me-2"></i>
            Transfer Now
          </Button>
        </Modal.Footer>
      </Form>
      
      <style>{`
        .custom-modal-width .modal-dialog {
          max-width: 450px;
        }
        
        .custom-modal-width .modal-body::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-modal-width .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .custom-modal-width .modal-body::-webkit-scrollbar-thumb {
          background: #667eea;
          border-radius: 10px;
        }
        
        .custom-modal-width .modal-body::-webkit-scrollbar-thumb:hover {
          background: #5568d3;
        }
      `}</style>
    </Modal>
  );
};

export default UPITransferModal;
